import { useState } from 'react';
import api from '../lib/api';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from './ui/dropdown-menu';
import { ClipboardCheck, ChevronDown } from 'lucide-react';

const STATUS_META = {
  aangevraagd: { label: 'Keuring aangevraagd', cls: 'bg-blue-100 text-blue-700 border-blue-300' },
  ingepland: { label: 'Keuring ingepland', cls: 'bg-amber-100 text-amber-700 border-amber-300' },
  goedgekeurd: { label: 'Keuring goedgekeurd', cls: 'bg-green-100 text-green-700 border-green-300' },
  afgekeurd: { label: 'Keuring afgekeurd', cls: 'bg-red-100 text-red-700 border-red-300' },
};

export const KeuringStatusControl = ({ installation, onUpdated, endpoint, size = 'sm' }) => {
  const putUrl = endpoint || `/scheduled-installations/${installation.id}/keuring`;
  const kr = installation.keuring_request;
  const status = kr?.status && kr.status !== 'niet_aangevraagd' ? kr.status : null;
  const [dialogOpen, setDialogOpen] = useState(false);
  const [reference, setReference] = useState('');
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);
  const [dateDialogOpen, setDateDialogOpen] = useState(false);
  const [keuringDate, setKeuringDate] = useState('');
  const source = (endpoint || '').includes('/schemas/') ? 'schema' : 'installation';
  const EMPTY_FORM = { customer_name: '', email: '', phone: '', street: '', postal_code: '', city: '', installation_type: '', remarks: '', item_id: '', typewoning: '3' };
  const [form, setForm] = useState(EMPTY_FORM);
  const [products, setProducts] = useState([]);
  const [attachments, setAttachments] = useState([]);

  const openAanvraag = async () => {
    setReference('');
    setForm(EMPTY_FORM);
    setAttachments([]);
    setDialogOpen(true);
    try {
      const [res, prodRes] = await Promise.all([
        api.get(`/spoq/prefill?source=${source}&source_id=${installation.id}`),
        api.get('/spoq/products'),
      ]);
      const prods = prodRes.data.products || [];
      setProducts(prods);
      const def = prods.find(p => /elektr/i.test(p.title)) || prods.find(p => /keuring/i.test(p.title)) || prods[0];
      const { attachments: att, ...pre } = res.data;
      setAttachments(att || []);
      setForm(p => ({ ...p, ...pre, item_id: def ? def.item_id : '' }));
    } catch { /* leeg formulier blijft bruikbaar */ }
  };

  const submitAanvraag = async () => {
    if (!form.customer_name || !form.street) { toast.error('Vul minstens klantnaam en adres in'); return; }
    if (!form.item_id) { toast.error('Kies een SPOQ-keuringstype'); return; }
    setSaving(true);
    try {
      const res = await api.post('/spoq/keuring-aanvraag', { source, source_id: installation.id, ...form });
      if (res.data.sent) {
        const ref = reference || res.data.dossier_nr || '';
        const upd = await api.put(putUrl, { status: 'aangevraagd', spoq_reference: ref, notes: form.remarks });
        onUpdated(upd.data.keuring_request);
        toast.success(`Keuringsaanvraag verzonden naar SPOQ${res.data.dossier_nr ? ` — dossier ${res.data.dossier_nr}` : ''}`);
        setDialogOpen(false);
      } else {
        toast.error(res.data.error || 'SPOQ weigerde de aanvraag — controleer de gegevens');
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Aanvraag versturen mislukt');
    } finally {
      setSaving(false);
    }
  };

  const update = async (payload, successMsg) => {
    setSaving(true);
    try {
      const res = await api.put(putUrl, payload);
      onUpdated(res.data.keuring_request);
      toast.success(successMsg);
      setDialogOpen(false);
    } catch (e) {
      toast.error('Kon keuringstatus niet bijwerken');
    } finally {
      setSaving(false);
    }
  };

  if (!status) {
    return (
      <>
        <Button variant="outline" size="sm" className={size === 'sm' ? 'h-6 px-2 text-[11px]' : ''}
          onClick={openAanvraag}
          data-testid={`keuring-aanvragen-${installation.id}`}>
          <ClipboardCheck className={size === 'sm' ? 'h-3 w-3 mr-1' : 'w-4 h-4 mr-1.5'} /> Keuring aanvragen
        </Button>
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto" data-testid="keuring-dialog">
            <DialogHeader><DialogTitle>Keuring aanvragen bij SPOQ</DialogTitle></DialogHeader>
            <p className="text-sm text-muted-foreground">Controleer en pas de gegevens aan. Pas bij "Verzenden" gaat de aanvraag naar SPOQ.</p>
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-sm font-medium">Klantnaam *</label>
                  <Input value={form.customer_name} onChange={(e) => setForm(p => ({ ...p, customer_name: e.target.value }))} data-testid="spoq-name-input" />
                </div>
                <div>
                  <label className="text-sm font-medium">Telefoon</label>
                  <Input value={form.phone} onChange={(e) => setForm(p => ({ ...p, phone: e.target.value }))} data-testid="spoq-phone-input" />
                </div>
              </div>
              <div>
                <label className="text-sm font-medium">E-mail</label>
                <Input value={form.email} onChange={(e) => setForm(p => ({ ...p, email: e.target.value }))} data-testid="spoq-email-input" />
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div className="col-span-2">
                  <label className="text-sm font-medium">Straat + nr *</label>
                  <Input value={form.street} onChange={(e) => setForm(p => ({ ...p, street: e.target.value }))} data-testid="spoq-street-input" />
                </div>
                <div>
                  <label className="text-sm font-medium">Postcode</label>
                  <Input value={form.postal_code} onChange={(e) => setForm(p => ({ ...p, postal_code: e.target.value }))} data-testid="spoq-postal-input" />
                </div>
              </div>
              <div>
                <label className="text-sm font-medium">Gemeente</label>
                <Input value={form.city} onChange={(e) => setForm(p => ({ ...p, city: e.target.value }))} data-testid="spoq-city-input" />
              </div>
              <div>
                <label className="text-sm font-medium">SPOQ-keuringstype *</label>
                <select className="w-full h-9 rounded-md border border-input bg-background px-3 text-sm"
                  value={form.item_id} onChange={(e) => setForm(p => ({ ...p, item_id: e.target.value }))} data-testid="spoq-product-select">
                  <option value="">{products.length ? 'Kies keuringstype...' : 'Producten laden mislukt — probeer opnieuw'}</option>
                  {products.map(p => <option key={p.item_id} value={p.item_id}>{p.title}</option>)}
                </select>
              </div>
              <div>
                <label className="text-sm font-medium">Type woning</label>
                <select className="w-full h-9 rounded-md border border-input bg-background px-3 text-sm"
                  value={form.typewoning} onChange={(e) => setForm(p => ({ ...p, typewoning: e.target.value }))} data-testid="spoq-typewoning-select">
                  <option value="1">Studio</option>
                  <option value="2">Appartement</option>
                  <option value="3">Halfopen of gesloten bebouwing</option>
                  <option value="4">Open bebouwing (&lt; 300 m²)</option>
                  <option value="5">Open bebouwing (&gt; 300 m²)</option>
                </select>
              </div>
              <div>
                <label className="text-sm font-medium">Type installatie</label>
                <Input value={form.installation_type} onChange={(e) => setForm(p => ({ ...p, installation_type: e.target.value }))} data-testid="spoq-type-input" />
              </div>
              <div>
                <label className="text-sm font-medium">Opmerkingen (optioneel)</label>
                <Textarea value={form.remarks} onChange={(e) => setForm(p => ({ ...p, remarks: e.target.value }))} rows={2} data-testid="spoq-remarks-input" />
              </div>
              <div>
                <label className="text-sm font-medium">SPOQ-referentie / dossiernummer (optioneel)</label>
                <Input value={reference} onChange={(e) => setReference(e.target.value)} placeholder="bv. SPOQ-2026-12345" data-testid="keuring-reference-input" />
              </div>
              {attachments.length > 0 && (
                <div className="rounded-md border bg-muted/40 px-3 py-2 text-xs" data-testid="spoq-attachments-info">
                  <span className="font-medium">Wordt automatisch meegestuurd:</span>
                  <ul className="list-disc ml-4 mt-1">
                    {attachments.map((a, i) => <li key={i}>{a}</li>)}
                  </ul>
                </div>
              )}
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setDialogOpen(false)}>Annuleren</Button>
              <Button disabled={saving} onClick={submitAanvraag} data-testid="keuring-submit-btn">
                {saving ? 'Verzenden...' : 'Verzenden naar SPOQ'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </>
    );
  }

  const meta = STATUS_META[status];
  const fmtD = (d) => d ? new Date(d).toLocaleDateString('nl-BE') : null;
  return (
    <>
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button className={`inline-flex items-center gap-1 rounded-full border font-medium ${meta.cls} ${size === 'sm' ? 'px-2 py-0.5 text-[11px]' : 'px-3 py-1.5 text-sm'}`}
          data-testid={`keuring-status-${installation.id}`}>
          <ClipboardCheck className="h-3 w-3" /> {meta.label}
          {status === 'ingepland' && kr?.scheduled_date ? ` · ${fmtD(kr.scheduled_date)}` : ''}
          {kr?.spoq_reference ? ` · ${kr.spoq_reference}` : ''}
          <ChevronDown className="h-3 w-3" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start">
        {Object.entries(STATUS_META).map(([key, m]) => (
          <DropdownMenuItem key={key} disabled={key === status && key !== 'ingepland'}
            onClick={() => {
              if (key === 'ingepland') {
                setKeuringDate(kr?.scheduled_date || '');
                setDateDialogOpen(true);
              } else {
                update({ status: key }, `Status: ${m.label}`);
              }
            }} data-testid={`keuring-set-${key}`}>
            {m.label}{key === 'ingepland' ? ' (met datum)' : ''}
          </DropdownMenuItem>
        ))}
        <DropdownMenuSeparator />
        <DropdownMenuItem className="text-destructive" onClick={() => update({ status: 'niet_aangevraagd' }, 'Keuringsaanvraag gereset')} data-testid="keuring-reset">
          Reset (niet aangevraagd)
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
    <Dialog open={dateDialogOpen} onOpenChange={setDateDialogOpen}>
      <DialogContent className="max-w-sm" data-testid="keuring-date-dialog">
        <DialogHeader><DialogTitle>Keuring inplannen</DialogTitle></DialogHeader>
        <div>
          <label className="text-sm font-medium">Keuringsdatum</label>
          <Input type="date" value={keuringDate} onChange={(e) => setKeuringDate(e.target.value)} data-testid="keuring-date-input" />
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setDateDialogOpen(false)}>Annuleren</Button>
          <Button disabled={saving || !keuringDate}
            onClick={async () => {
              await update({ status: 'ingepland', scheduled_date: keuringDate }, `Keuring ingepland op ${fmtD(keuringDate)}`);
              setDateDialogOpen(false);
            }} data-testid="keuring-date-save-btn">
            Inplannen
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
    </>
  );
};
